import { useEffect, useRef, useState } from 'react';
import { Maximize2, X } from 'lucide-react';
import { c, t } from '../../theme';

// A free-text "Notes"-style table cell that can expand into a larger
// textarea for comfortable typing and reading -- the single-line <input>
// this replaces forced horizontal scrolling (or squinting) for anything
// longer than a few words (Jesse, Nov 2026). Same save-on-blur contract as
// every other editable field in this module: value/onChange are controlled
// by the caller, onBlur fires the actual save -- this component only adds
// the bigger view on top.
//
// The expanded view is a fixed-position modal, not a popover anchored to the
// cell -- every table in this module sits inside ExpandableScroll's own
// `overflow: auto` (and Section's `overflow: hidden`), so an absolutely
// positioned popover would get clipped at the scroll container's edge.
// `position: fixed` escapes that entirely.
export default function NotesCell({ value, onChange, onBlur, canEdit, placeholder = 'Add a note…' }) {
  const [expanded, setExpanded] = useState(false);

  function close() {
    if (canEdit) onBlur();
    setExpanded(false);
  }

  // The Escape listener below only (re)subscribes when `expanded` flips, so
  // without this ref its closure would keep calling the `close` from the
  // render where the modal opened -- stale `onBlur`/`value` from before any
  // typing happened, so Escape would silently skip the save.
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded]);

  if (!canEdit) {
    if (!value) return '—';
    return (
      <>
        <span style={s.readText} onClick={() => setExpanded(true)} title="Click to read the full note">{value}</span>
        {expanded && (
          <div style={s.overlay} onMouseDown={close}>
            <div style={s.modal} onMouseDown={(e) => e.stopPropagation()}>
              <div style={s.modalHead}>
                <span style={s.modalTitle}>Note</span>
                <button type="button" style={s.closeBtn} onClick={close}><X size={14} /></button>
              </div>
              <div style={s.modalText}>{value}</div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div style={s.wrap}>
      <input
        type="text" style={s.input} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} onBlur={onBlur}
      />
      <button type="button" style={s.expandBtn} title="Expand" onClick={() => setExpanded(true)}>
        <Maximize2 size={11} />
      </button>
      {expanded && (
        <div style={s.overlay} onMouseDown={close}>
          <div style={s.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={s.modalHead}>
              <span style={s.modalTitle}>Note</span>
              <button type="button" style={s.closeBtn} onClick={close}><X size={14} /></button>
            </div>
            <textarea
              autoFocus rows={10} style={s.textarea} value={value} placeholder={placeholder}
              onChange={(e) => onChange(e.target.value)} onBlur={onBlur}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap:      { position: 'relative', display: 'flex', alignItems: 'center', gap: '4px', width: '100%' },
  input:     { flex: 1, minWidth: '140px', padding: '4px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '4px', fontSize: t.caption.fontSize, fontFamily: 'inherit' },
  expandBtn: { flexShrink: 0, border: 'none', background: 'transparent', color: c.textFaint, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '2px' },
  readText:  { cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.35)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
  },
  modal: {
    background: c.surface, border: `1px solid ${c.borderStrong}`, borderRadius: '10px',
    boxShadow: '0 16px 48px rgba(0,0,0,0.25)', padding: '14px 16px', width: '420px', maxWidth: '100%',
  },
  modalHead:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' },
  modalTitle: { fontSize: t.caption.fontSize, fontWeight: 700, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.03em' },
  closeBtn:   { border: 'none', background: 'transparent', color: c.textFaint, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '2px' },
  textarea:   { width: '100%', minHeight: '160px', padding: '8px 10px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontSize: t.bodySm.fontSize, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' },
  modalText:  { fontSize: t.bodySm.fontSize, color: c.text, whiteSpace: 'pre-wrap', lineHeight: 1.6, maxHeight: '60vh', overflowY: 'auto' },
};
